import { Badge, Switch, TogglePill } from '@civilgrid/ui/components';
import {
  FEATURE_DEFAULTS,
  type FeatureBillingTier,
  isBillingTierEnabled,
  isTierDisableable,
  NON_CONFIGURABLE_FEATURES,
  ProjectFeature,
} from '@civilgrid/utils/src/common/featureAccess';
import type { ReactNode } from 'react';
import { FEATURE_META } from './featureMeta';

export type ViewMode = 'defaults' | 'org';

const TIERS: { tier: FeatureBillingTier; label: string; sublabel: string }[] = [
  { tier: 'draft', label: 'Draft', sublabel: 'NEW' },
  { tier: 'proposal', label: 'Project Lite', sublabel: 'ENTRY' },
  { tier: 'project', label: 'Project', sublabel: 'CORE' },
  { tier: 'projectPlus', label: 'Plus', sublabel: 'FULL' },
];

// Display order (configurable features first, in mockup priority order). The two non-configurable
// features (CivilSearch, Digitization) are pinned to the bottom — they render greyed for reference
// only since their tier availability is hardcoded.
const FEATURE_ORDER: ProjectFeature[] = [
  ProjectFeature.Utilities,
  ProjectFeature.Downloads,
  ProjectFeature.FieldNotes,
  ProjectFeature.Potholing,
  ProjectFeature.Intelligence,
  ProjectFeature.LandRights,
  ProjectFeature.Zoning,
  ProjectFeature.AntennaMap,
  ProjectFeature.EnvironmentalStudies,
  ProjectFeature.Geotechnical,
  ProjectFeature.Environmental,
  ProjectFeature.UtilitiesReports,
  ProjectFeature.UtilitiesNotifications,
  ProjectFeature.ProposalPacket,
  ProjectFeature.Billing,
  ProjectFeature.Summary,
  // Non-configurable (reference only) — kept at the bottom.
  ProjectFeature.CivilSearch,
  ProjectFeature.Digitization,
];

interface Props {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  orgName: string;
  featureAccess: Partial<Record<string, FeatureBillingTier[]>>;
  enabledBillingTiers: Partial<Record<string, boolean>>;
  onToggleFeature: (feature: ProjectFeature, tier: FeatureBillingTier) => void;
  onToggleTier: (tier: FeatureBillingTier, enabled: boolean) => void;
  /** Save control (button + unsaved indicator), rendered in the toolbar above the table. */
  saveSlot?: ReactNode;
}

export default function FeatureAccessEditor({
  viewMode,
  onViewModeChange,
  orgName,
  featureAccess,
  enabledBillingTiers,
  onToggleFeature,
  onToggleTier,
  saveSlot,
}: Props) {
  const isOrgView = viewMode === 'org';
  // Delegate to the shared resolver so non-disableable tiers (draft) ignore any stale stored
  // `false` override and never render visually dimmed.
  const tierEnabled = (tier: FeatureBillingTier) => isBillingTierEnabled(tier, { enabledBillingTiers });

  const helpText = isOrgView ? (
    <div className="flex flex-wrap items-center gap-2 text-sm text-grey-90">
      <span>This organization's feature access. Click any cell to change it for {orgName} only.</span>
      <Badge kind="outline" color="yellow" className="gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-yellow-60" /> On
      </Badge>
      <span>differs from the tier default</span>
    </div>
  ) : (
    <div className="flex items-center gap-2 text-sm text-grey-90">
      <span>Read-only platform defaults. Switch to "Custom Settings" to override for {orgName}.</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="feature-access-editor">
      {/* Segmented control */}
      <div className="inline-flex rounded-md border border-grey-30 p-0.5 text-sm">
        <button
          type="button"
          data-testid="feature-view-defaults"
          onClick={() => onViewModeChange('defaults')}
          className={`flex items-center gap-1 rounded px-3 py-1 ${viewMode === 'defaults' ? 'bg-grey-10 font-medium' : 'text-grey-90'}`}
        >
          Tier Defaults <span aria-hidden>🔒</span>
        </button>
        <button
          type="button"
          data-testid="feature-view-org"
          onClick={() => onViewModeChange('org')}
          className={`rounded px-3 py-1 ${isOrgView ? 'bg-blue-10 font-medium text-blue-80' : 'text-grey-90'}`}
        >
          Custom Settings
        </button>
      </div>

      {/* Toolbar: help text + save, above the table */}
      <div className="flex items-start justify-between gap-4">
        {helpText}
        {saveSlot ? <div className="shrink-0">{saveSlot}</div> : null}
      </div>

      {/* Matrix */}
      <div className="overflow-hidden rounded-lg border border-grey-30">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-grey-30">
              <th className="px-4 py-3 text-left font-medium text-grey-90">Feature</th>
              {TIERS.map(({ tier, label, sublabel }) => {
                const enabled = tierEnabled(tier);
                return (
                  <th key={tier} className={`px-4 py-3 text-center ${!enabled ? 'opacity-50' : ''}`}>
                    <div className={`font-semibold ${tier === 'draft' ? 'text-orange-60' : 'text-grey-90'}`}>
                      {label}
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-grey-90">{sublabel}</div>
                    {/* draft (and any non-disableable tier) is always enabled — no toggle shown. */}
                    {isOrgView && isTierDisableable(tier) && (
                      <div className="mt-1 flex justify-center">
                        <Switch
                          data-testid={`tier-enabled-${tier}`}
                          checked={enabled}
                          onCheckedChange={(c: boolean) => onToggleTier(tier, c)}
                        />
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {FEATURE_ORDER.map((feature) => {
              const meta = FEATURE_META[feature];
              const defaults = FEATURE_DEFAULTS[feature];
              const override = featureAccess[feature];
              // Non-configurable features (CivilSearch/Digitization) are shown for reference only —
              // greyed and non-interactive, since their tier availability is permanently hardcoded.
              const configurable = !NON_CONFIGURABLE_FEATURES.has(feature);
              return (
                <tr key={feature} className="border-b border-grey-20 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className={`font-medium ${configurable ? 'text-grey-90' : 'text-grey-50'}`}>{meta.label}</div>
                    <div className={`text-xs ${configurable ? 'text-grey-90' : 'text-grey-50'}`}>
                      {meta.description}
                    </div>
                    <div className={`mt-0.5 text-xs italic ${configurable ? 'text-grey-90' : 'text-grey-50'}`}>
                      {meta.effect}
                    </div>
                  </td>
                  {TIERS.map(({ tier }) => {
                    const def = defaults.includes(tier);
                    const eff = isOrgView ? (override ?? defaults).includes(tier) : def;
                    const isOverride = configurable && isOrgView && eff !== def;
                    const locked = !configurable || !isOrgView || !tierEnabled(tier);
                    return (
                      <td key={tier} className="px-4 py-3 text-center">
                        <TogglePill
                          on={eff}
                          accent={isOverride}
                          disabled={locked}
                          className={configurable ? undefined : 'border-grey-30 bg-grey-10 text-grey-60'}
                          data-testid={`feature-${feature}-${tier}`}
                          onClick={() => onToggleFeature(feature, tier)}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
